import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function ApiKeyManager({ partnerView = false }) {
  const [keys, setKeys]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey]   = useState(null);
  const [form, setForm] = useState({ name: '', environment: 'sandbox', rate_limit_per_min: 60, rate_limit_per_day: 10000 });

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/tenant/api-keys'); setKeys(r.data); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to load keys'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Key name is required'); return; }
    try {
      const r = await api.post('/tenant/api-keys', form);
      setNewKey(r.data.raw_key);
      setCreating(false);
      setForm({ name: '', environment: 'sandbox', rate_limit_per_min: 60, rate_limit_per_day: 10000 });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    try { await api.delete(`/tenant/api-keys/${id}`); toast.success('Key revoked'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const S = {
    card:  { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 },
    key:   { fontFamily: 'monospace', background: 'var(--bg)', padding: '8px 12px', borderRadius: 8, fontSize: 13, wordBreak: 'break-all', marginTop: 8, border: '1px solid var(--border)' },
    input: { width: '100%', marginBottom: 12 },
    badge: (env) => ({ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: env === 'production' ? '#a6e3a122' : '#89b4fa22', color: env === 'production' ? '#a6e3a1' : '#89b4fa', letterSpacing: 1 }),
    status: (s) => ({ color: s === 'active' ? '#a6e3a1' : '#f38ba8', fontSize: 12, fontWeight: 600 }),
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>API Keys</h2>
        {!partnerView && <button onClick={() => setCreating(!creating)} className="btn btn-primary">＋ Create Key</button>}
      </div>

      {/* One-time key reveal */}
      {newKey && (
        <div style={{ ...S.card, borderColor: '#a6e3a1' }}>
          <div style={{ color: '#a6e3a1', fontWeight: 600, marginBottom: 8 }}>⚠ Copy your API key — it will not be shown again</div>
          <div style={S.key}>{newKey}</div>
          <button onClick={() => { navigator.clipboard.writeText(newKey); toast.success('Copied!'); }} className="btn btn-ghost" style={{ marginTop: 8, fontSize: 12 }}>Copy to clipboard</button>
          <button onClick={() => setNewKey(null)} className="btn btn-ghost" style={{ marginTop: 8, fontSize: 12, marginLeft: 8 }}>I've saved it ✓</button>
        </div>
      )}

      {/* Create form */}
      {creating && (
        <div style={S.card}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>New API Key</h3>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Key name *</span>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Production Integration" className="input" style={S.input} />
          </label>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Environment</span>
            <select value={form.environment} onChange={e => setForm(f => ({ ...f, environment: e.target.value }))} className="input" style={S.input}>
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              <span style={{ fontSize: 12, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Rate limit / minute</span>
              <input type="number" value={form.rate_limit_per_min} onChange={e => setForm(f => ({ ...f, rate_limit_per_min: parseInt(e.target.value) }))} className="input" style={{ width: '100%' }} />
            </label>
            <label>
              <span style={{ fontSize: 12, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Rate limit / day</span>
              <input type="number" value={form.rate_limit_per_day} onChange={e => setForm(f => ({ ...f, rate_limit_per_day: parseInt(e.target.value) }))} className="input" style={{ width: '100%' }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={create} className="btn btn-primary">Create key</button>
            <button onClick={() => setCreating(false)} className="btn btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      {/* Keys list */}
      {loading ? <div style={{ color: 'var(--text-3)', padding: 40, textAlign: 'center' }}>Loading…</div> : (
        keys.length === 0 ? (
          <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-3)', padding: 40 }}>No API keys yet. Create one to get started.</div>
        ) : (
          keys.map(k => (
            <div key={k.id} style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <strong style={{ fontSize: 14 }}>{k.name}</strong>
                    <span style={S.badge(k.environment)}>{k.environment.toUpperCase()}</span>
                    <span style={S.status(k.status)}>{k.status}</span>
                  </div>
                  <code style={{ fontSize: 12, color: 'var(--text-3)' }}>{k.key_prefix}••••••••••••••</code>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                    {k.rate_limit_per_min}/min · {k.rate_limit_per_day.toLocaleString()}/day · {k.total_calls?.toLocaleString() || 0} total calls
                    {k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                  </div>
                </div>
                {!partnerView && k.status === 'active' && (
                  <button onClick={() => revoke(k.id)} className="btn btn-ghost" style={{ fontSize: 12, color: '#f38ba8', borderColor: '#f38ba833' }}>Revoke</button>
                )}
              </div>
            </div>
          ))
        )
      )}
    </div>
  );
}
